import { WorkflowEventTypes, WorkingCopyKey } from '@amzn/awscat-pattern-library-service-model';
import DraftViewerRole from '../../../components/utils/DraftViewerRole';
import BrowserHistory from '../../utils/BrowserHistory';
import { createWorkflowEventWithStatePolling } from '../../../api/CreateWorkflowEventWithStatePolling';
import RouterPaths from '../../../common/RouterPaths';
import EditContentData from './EditContentData';
import { filterInlineCommentsToUpdate } from '../../utils/InlineCommentUtils';
import LocalStorage from '../../../common/LocalStorage';
import {
  contentChecklistListName,
  reviewerChecklistListName,
  shouldResetChecklist,
} from './ContentUtils';

/**
 * DRAFT-SPECIFIC edit view of content.
 *
 * Extends EditContentData (which renders the full edit page with tabs:
 * Details/Content/Code/Attachments/Authors/Reviewers/History, plus working copy
 * management, validation, save/discard controls, etc.)
 *
 * This subclass overrides the three "action" hooks that EditContentData leaves abstract:
 *   - saveChanges()                    → persist draft edits + inline comments
 *   - discardChanges()                 → throw away edits + unlock workflow
 *   - exitEditModeAndSubmitForReview() → discard working copy + submit for review + navigate
 *
 * It also adds postContentTypeChanges() for updating comment section mappings after
 * a content type conversion (e.g. Pattern → Guide).
 *
 * Inheritance chain:
 *   BaseContentData (shared utilities: clone, history, navigation, tab handling)
 *     └── EditContentData (full edit page: tabs, working copy, validation, UI)
 *           └── DraftEditContentData (THIS: draft-specific save/discard/submit logic)
 */
class DraftEditContentData extends EditContentData {
  // Use the "contentworkingcopy" key for localStorage/IndexedDB working copies.
  // Other subclasses (e.g. for finalization) use different keys to avoid collisions.
  protected workingCopyKey = WorkingCopyKey.contentworkingcopy;

  /**
   * SAVE CHANGES to the draft.
   *
   * This is the most complex operation — it needs to:
   * 1. Check if the reviewer checklist should be reset (content changed meaningfully)
   * 2. Update content audience if it changed (separate API call)
   * 3. Fetch the latest working copy to get any inline comment position updates
   * 4. Persist the content update to the backend (with optional unlock)
   * 5. Update inline comment positions that shifted due to content edits
   * 6. Apply any content-type-specific changes (comment section conversions)
   * 7. Clean up the local working copy (no longer needed after save)
   * 8. If unlocking, notify the parent to exit edit mode and refresh data
   *
   * @param unlock - If true, release the edit lock after saving (user clicked "Save & Close")
   */
  // @ts-expect-error TS2416 - strict migration
  protected saveChanges = async (unlock: boolean) => {
    const { content, contentId, contentAudience, onDisableEditMode, workflow, workflowId } =
      this.props;
    const { updatedContent, updatedContentAudience } = this.state;

    // Step 1: Determine if the reviewer's checklist should be reset.
    // If the author made meaningful changes (not just formatting), reviewers need to
    // re-check their checklist items. Uses shouldResetChecklist() which compares
    // content and audience for substantive differences.
    const resetReviewerChecklist = shouldResetChecklist(
      content,
      updatedContent,
      contentAudience,
      updatedContentAudience,
    );

    if (resetReviewerChecklist) {
      LocalStorage.resetChecklistState(contentId, reviewerChecklistListName);
    }

    // Step 2: If audience changed (e.g. Internal → External), update it separately.
    // This is a separate API because audience affects permissions and visibility.
    if (contentAudience !== updatedContentAudience) {
      await this.mplService.updateWorkflowContentAudience(contentId, updatedContentAudience);
    }

    // Step 3: Get the most recent local working copy to extract inline comment
    // position updates that were tracked during editing.
    const mostRecentWorkingCopy = await this.workingCopyMngr.getMostRecentWorkingCopy(
      this.props.contentId,
      this.workingCopyKey,
    );

    // Fetch all current inline comments from backend to compare against
    const { inlineComments: allInlineComments } = await this.mplService.listInlineComments(
      contentId,
      workflowId,
    );

    // Step 4: Persist the content update. unlock=true releases the edit lock.
    await this.mplService.updateContent(contentId, updatedContent, unlock);

    // Step 5: Update inline comment positions that shifted due to content edits.
    // filterInlineCommentsToUpdate() compares the working copy's tracked comment
    // positions against the current backend state to find comments that moved.
    const updatedInlineComments = filterInlineCommentsToUpdate(
      // @ts-expect-error TS2345 - strict migration
      mostRecentWorkingCopy?.workingCopy?.updatedInlineComments,
      allInlineComments,
    );
    if (updatedInlineComments?.length) {
      const updateCommentAttributes = updatedInlineComments.map((comment) => ({
        commentId: comment.commentId,
        startOffset: comment.startOffset,
        endOffset: comment.endOffset,
        textData: comment.textData,
      }));
      await this.mplService.updateInlineComments(
        contentId,
        // @ts-expect-error TS18048 - strict migration
        workflow.draftWorkflowId,
        updateCommentAttributes,
      );
    }

    // Step 6: If content type was converted (e.g. Pattern → Guide), update
    // the comment-to-section mappings so comments stay attached to the right sections.
    await this.postContentTypeChanges();

    // Step 7: Clean up local working copy — it's been persisted to the backend now.
    await this.workingCopyMngr.deleteWorkingCopies(contentId, this.workingCopyKey);

    this.setState({
      formError: null,
      isContentNewOrModified: true,
      isContentUnchanged: true,
    });

    // Step 8: If unlocking (Save & Close), tell the parent to exit edit mode.
    // This triggers ContentViewer.editModeDisabled() → reloadData().
    if (unlock) {
      if (onDisableEditMode) {
        await onDisableEditMode();
      }
    }
  };

  /**
   * DISCARD CHANGES and exit edit mode.
   *
   * Called when user clicks "Discard" — throws away all unsaved edits:
   * 1. Check if the content checklist should be reset (edits were meaningful)
   * 2. Unlock the workflow (release the edit lock so others can edit)
   * 3. Delete the local working copy
   * 4. Reset the content checklist if needed
   * 5. Notify parent to exit edit mode and refresh
   */
  // @ts-expect-error TS2416 - strict migration
  protected discardChanges = async () => {
    const { contentId, workflow, onDisableEditMode, content, contentAudience } = this.props;
    const { updatedContent, updatedContentAudience } = this.state;

    // Check if checklist needs reset BEFORE discarding (compare current edits vs original)
    const resetContentChecklist = shouldResetChecklist(
      content,
      updatedContent,
      contentAudience,
      updatedContentAudience,
    );

    // Release the edit lock so other users can edit
    // @ts-expect-error TS18048 - strict migration
    await this.mplService.unlockWorkflow(workflow.contentId);

    // Delete the local working copy (auto-saved draft)
    await this.workingCopyMngr.deleteWorkingCopies(contentId, this.workingCopyKey);

    this.setState({
      isContentUnchanged: true,
    });

    // Reset the author's content checklist if the edits were meaningful
    if (resetContentChecklist) {
      LocalStorage.resetChecklistState(contentId, contentChecklistListName);
    }

    // Tell parent to exit edit mode and refresh data
    if (onDisableEditMode) {
      await onDisableEditMode();
    }
  };

  /**
   * EXIT EDIT MODE AND SUBMIT FOR REVIEW.
   *
   * Called when user clicks "Submit for Review" from within edit mode.
   * This is a compound operation:
   * 1. Discard the working copy and unlock (via discardChanges)
   * 2. Notify parent that submission is starting (shows loading state)
   * 3. Send SEND_FOR_REVIEW event to the draft workflow
   * 4. Notify parent that submission completed
   * 5. Navigate to the appropriate dashboard based on the user's role
   */
  // @ts-expect-error TS2416 - strict migration
  protected exitEditModeAndSubmitForReview = async () => {
    const { contentId, workflow, onSubmitForReview, onSubmitForReviewComplete, draftViewerRole } =
      this.props;

    // Step 1: Exit edit mode — discard working copy and unlock
    await this.discardChanges();

    // Step 2: Notify parent to show loading state on submit button
    if (onSubmitForReview) {
      onSubmitForReview();
    }

    // Step 3: Send the SEND_FOR_REVIEW event to transition the workflow state.
    // Uses polling to wait for the state change to be reflected (eventual consistency).
    await createWorkflowEventWithStatePolling(
      contentId,
      // @ts-expect-error TS18048 - strict migration
      workflow.draftWorkflowId,
      WorkflowEventTypes.SEND_FOR_REVIEW,
      '',
    );

    // Step 4: Clear loading state
    if (onSubmitForReviewComplete) {
      onSubmitForReviewComplete();
    }

    // Step 5: Navigate to the appropriate dashboard based on who submitted.
    // Each role has a different "home" page they should return to after submitting.
    switch (draftViewerRole) {
      case DraftViewerRole.Admin:
        BrowserHistory.push(RouterPaths.ADMIN_DASHBOARD, { keepFlashbarMessages: true });
        break;
      case DraftViewerRole.TechWriter:
        BrowserHistory.push(RouterPaths.TECH_WRITER_DASHBOARD_NEW);
        break;
      case DraftViewerRole.Author:
        BrowserHistory.push(RouterPaths.HOME_PAGE);
        break;
      default:
        BrowserHistory.push(RouterPaths.HOME_PAGE);
    }
  };

  /**
   * Post-save hook for content type conversions.
   *
   * When content is converted between types (e.g. Pattern → Guide), inline comments
   * may need their section mappings updated because sections get reorganized.
   * The commentConversions state tracks these mappings during the conversion process.
   */
  protected postContentTypeChanges = async () => {
    const { contentId, workflowId } = this.props;
    const { commentConversions } = this.state;
    if (commentConversions && commentConversions.length > 0) {
      await this.mplService.updateCommentSections(contentId, workflowId, commentConversions);
    }
  };
}

export default DraftEditContentData;
